import { Marker } from "react-map-gl";
import {
    BorderCrossing,
    OvernightStay,
    PitStopStay,
    RouteMetadata,
    RoutePoint,
    RouteRequestState,
    RouteSegmentPointsList,
    WeatherDelay
} from "../../generated/gpb/api/route";
import { Circle } from "@mui/icons-material";
import { routeRVIconScale } from "../../app/constants";
import React, { useEffect, useMemo, useState } from "react";
import { getRVSymbol } from "./GeoRvSymbol";
import MapMarkers from "./GeoMarkerTemplate";
import {
    fetchAndSetPlaceNames,
    getBorderCrossingMarkerData,
    getDelayMarkerData,
    getFirstOrFinalMarker,
    getPitStopMarkerData,
    getStayMarkerData
} from "./GeoMarkerUtils";
import { OpenRoadsGeoMarker } from "./OpenRoadsMarkers/OpenRoadsGeoMarkers";
import { useSelectedRoute, useTripRouteState } from "../../features/riskViewer/riskViewerSlice";
import { generatePoint } from "./geoUtils";
import { useShouldShowAlerts } from "../../features/alertDisplay/alertDisplaySlice";

type GeoMarkersProps = {
    routePoints: RouteSegmentPointsList,
    points: Array<RoutePoint> | null,
    selectedTime: number,
    routeMetadata: RouteMetadata | undefined,
};

/**
 * Component to generate points for a route
 */
export const GeoMarkers: React.FC<GeoMarkersProps> = ( { routePoints, points, selectedTime, routeMetadata } ) =>
{
    const point = useMemo( () => generatePoint( points, selectedTime ), [ points, selectedTime ] );
    const routeId = useSelectedRoute();
    const tripRouteState = useTripRouteState();
    const isShowingAlerts: boolean = useShouldShowAlerts();
    const isMultistartSelectionState = tripRouteState !== undefined
        ? RouteRequestState[tripRouteState].toString() === RouteRequestState.SHOW_OPTIONS.toString()
        : false;
    const [ pitStopLocationNames, setPitStopLocationNames ] = useState<Array<string | undefined> | undefined>( undefined );
    const [ overnightStayLocationNames, setOvernightStayLocationNames ] = useState<Array<string | undefined> | undefined>( undefined );
    const [ weatherDelayLocationNames, setWeatherDelayLocationNames ] = useState<Array<string | undefined> | undefined>( undefined );
    const [ borderCrossingLocationNames, setBorderCrossingLocationNames ] = useState<Array<string | undefined> | undefined>( undefined );

    const [
        weatherDelays,
        delayMarkers,
        overnightStays,
        pitStops,
        borderCrossings,
        overnightMarkers,
        finalMarker,
        firstMarker,
        pitStopMarkers,
        crossingMarkers,
    ] = useMemo( () =>
    {
        const stays: Array<OvernightStay> = [];
        const pitStopStays: Array<PitStopStay> = [];
        const delays: Array<WeatherDelay> = [];
        const crossings: Array<BorderCrossing> = [];
        if ( routePoints.segments.length === 0 )
        {
            // Handle the case where there are no segments
            return [ [], [], [], [], [], null, null, null ];
        }
        routePoints.segments.forEach( ( segs ) =>
        {
            segs.borderCrossings.forEach( ( crossing ) => crossings.push( crossing ) );
            segs.overnightStays.forEach( ( stay ) =>
            {
                stays.push( stay );
            } );
            segs.pitStopStays.forEach( ( stop ) =>
            {
                pitStopStays.push( stop );
            } );
            segs.weatherDelays.forEach( ( delay ) =>
            {
                delays.push( delay );
            } );
        } );
        // shrink the icon when the selected time is not the start
        const iconScale = routeRVIconScale * .75;

        //generate markers for stays, weather delays, and the final destination
        const last = routePoints.segments[routePoints.segments.length - 1]?.end;
        const first = routePoints.segments[0]?.start;
        const pitStopMapMarkers = (
            <MapMarkers
                items = {pitStopStays}
                iconScale = {iconScale}
                getMarkerData = {( pitStop, index ) => getPitStopMarkerData( pitStop, index, iconScale, pitStopLocationNames ? pitStopLocationNames[index] : "" )}
            />
        );

        const stayMarkers = (
            <MapMarkers
                items = {stays}
                iconScale = {iconScale}
                getMarkerData = {( stay, index ) => getStayMarkerData( stay, index, iconScale, overnightStayLocationNames ? overnightStayLocationNames[index] : "" )}
            />
        );

        const borderCrossingMarkers = (
            <MapMarkers
                items = {crossings}
                iconScale = {iconScale}
                getMarkerData = {( crossing, index ) => getBorderCrossingMarkerData( crossing, index, iconScale, borderCrossingLocationNames ? borderCrossingLocationNames[index] : "" )}
            />
        );

        const delayMapMarkers = (
            <MapMarkers
                items = {delays}
                iconScale = {iconScale}
                getMarkerData = {( delay, index ) => getDelayMarkerData( delay, index, iconScale, weatherDelayLocationNames ? weatherDelayLocationNames[index] : "" )}
            />
        );

        const firstMapMarker = first !== undefined ? (
            <MapMarkers
                items = {[ first ]}
                iconScale = {iconScale}
                getMarkerData = {( lastLocation: RoutePoint ) => getFirstOrFinalMarker( lastLocation, stays.length, iconScale, true )}
            />
        ) : null;

        const finalMapMarker = last !== undefined ? (
            <MapMarkers
                items = {[ last ]}
                iconScale = {iconScale}
                getMarkerData = {( lastLocation: RoutePoint ) => getFirstOrFinalMarker( lastLocation, stays.length, iconScale )}
            />
        ) : null;

        return [ delays, delayMapMarkers, stays, pitStopStays, crossings, stayMarkers, finalMapMarker, firstMapMarker, pitStopMapMarkers, borderCrossingMarkers ];
    }, [ routePoints, selectedTime, overnightStayLocationNames, weatherDelayLocationNames, pitStopLocationNames ] );

    useEffect( () =>
    {
        if ( overnightStays.length !== 0 && ( overnightStayLocationNames === undefined || overnightStayLocationNames.length === 0 ) )
        {
            fetchAndSetPlaceNames( overnightStays, setOvernightStayLocationNames );
        }
        else if ( overnightStays.length === 0 )
        {
            setOvernightStayLocationNames( undefined );
        }
    }, [ overnightStays, overnightStayLocationNames ] );

    useEffect( () =>
    {
        if ( pitStops.length !== 0 && ( pitStopLocationNames === undefined || pitStopLocationNames.length === 0 ) )
        {
            fetchAndSetPlaceNames( pitStops, setPitStopLocationNames );
        }
        else if ( pitStops.length === 0 )
        {
            setPitStopLocationNames( undefined );
        }
    }, [ pitStops, pitStopLocationNames ] );

    useEffect( () =>
    {
        if ( weatherDelays.length !== 0 && ( weatherDelayLocationNames === undefined || weatherDelayLocationNames.length === 0 ) )
        {
            fetchAndSetPlaceNames( weatherDelays, setWeatherDelayLocationNames );
        }
        else if ( weatherDelays.length === 0 )
        {
            setWeatherDelayLocationNames( undefined );
        }
    }, [ weatherDelays, weatherDelayLocationNames ] );

    useEffect( () =>
    {
        if ( borderCrossings.length !== 0 && ( borderCrossingLocationNames === undefined || borderCrossingLocationNames.length === 0 ) )
        {
            fetchAndSetPlaceNames( borderCrossings, setBorderCrossingLocationNames );
        }
        else if ( borderCrossings.length === 0 )
        {
            setBorderCrossingLocationNames( undefined );
        }
    }, [ borderCrossings, borderCrossingLocationNames ] );


    const rvSymbol = useMemo( () => getRVSymbol( overnightStays, pitStops, weatherDelays, borderCrossings, selectedTime, routeRVIconScale, routeMetadata ), [ selectedTime, weatherDelays, overnightStays, routeRVIconScale, routeMetadata ] );

    return (
        <React.Fragment>
            {delayMarkers}
            {overnightMarkers}
            {finalMarker}
            {pitStopMarkers}
            {firstMarker !== null && firstMarker}
            {crossingMarkers}
            {point !== undefined && !isShowingAlerts && (
                <Marker
                    latitude = {point?.coordinates[1]}
                    longitude = {point?.coordinates[0]}
                    anchor = "center"
                    // once the styling is added to overlap the icons, they offset
                    // this offset corrects that offset
                    offset = {[ -10, -10 ]}
                    // set the z index to keep the RV icon rendering in front of the other icons
                    style = {{ zIndex: 1, pointerEvents: "none" }}
                >
                    <div style = {{ pointerEvents: "none" }}>
                        <Circle
                            style = {{
                                color: "white",
                                position: "absolute",
                                scale: `${routeRVIconScale}%`,
                                pointerEvents: "none",
                            }}
                        />
                        {rvSymbol}
                    </div>
                </Marker>
            )}
            {routeId !== undefined && !isMultistartSelectionState &&
                <OpenRoadsGeoMarker routeId = {routeId} totalPoints = {routeMetadata?.pointCount}/>}
        </React.Fragment>
    );
};

